import type { ChatAction, ChatOption } from '../types/chat';
import { quest } from './quest';

/** Options leading into the quest disappear once it has been played */
/** …and while it's still available, the quest invitation always comes first so it's easy to spot */
export const questFilter = (options: ChatOption[]) => {
  if (quest.isUsed()) return options.filter((o) => !o.quest);
  const list = options.map(resumeQuestAsk);
  return [...list.filter((o) => o.quest), ...list.filter((o) => !o.quest)];
};

/**
 * Already asked "Why are you glitching?" (and said "Maybe later")? Don't make them sit through the
 * same story again: in every chat the option becomes "Help Pixel" and jumps straight to the
 * one-step confirmation ("REALLY?! … Ready?" → "Begin the hunt").
 */
function resumeQuestAsk(o: ChatOption): ChatOption {
  if (o.action?.type !== 'glitch' || !quest.isGlitching() || !o.next.endsWith('-ask')) return o;
  return { ...o, label: 'Help Pixel', next: o.next.replace(/-ask$/, '-confirm'), reaction: 'happy', action: undefined };
}

/** Window event the smooth scroller listens to for Pixel's guided glides (detail = CSS selector) */
export const GUIDED_SCROLL_EVENT = 'pixel:scrollto';
/** Scripted page scroll to an exact position (detail = { y, duration (s), accelerate }) — the lift-off */
export const SCRIPTED_SCROLL_EVENT = 'pixel:scrolly';

export interface ScriptedScroll {
  y: number;
  duration: number;
  /** Start slow and keep speeding up (a rocket rush) instead of a smooth glide */
  accelerate?: boolean;
}

/** Scroll the page for a scripted moment; jumps straight there when smooth scrolling is off */
export function scriptedScroll(detail: ScriptedScroll) {
  if (document.documentElement.classList.contains('lenis')) {
    window.dispatchEvent(new CustomEvent(SCRIPTED_SCROLL_EVENT, { detail }));
  } else {
    window.scrollTo({ top: detail.y, behavior: 'auto' });
  }
}

/** Carry out a chat action: start the quest or glide to a section. */
export function runPixelAction(action: ChatAction) {
  if (action.type === 'quest') {
    quest.start();
    return;
  }
  if (action.type === 'glitch') {
    quest.startGlitching();
    return;
  }
  const target = document.querySelector(action.target);
  if (!target) return;
  // The smooth scroller is off for reduced motion — jump straight there in that case
  if (document.documentElement.classList.contains('lenis')) {
    window.dispatchEvent(new CustomEvent(GUIDED_SCROLL_EVENT, { detail: action.target }));
  } else {
    target.scrollIntoView({ block: 'start' });
  }
}
